<template>
  <div v-if="hasTeamInfoLoaded" class="container">
    <div class="title">
      Subscribe for Access to Premium Features
    </div>
    <div class="plans-container">
      <div class="plan-container small">
        <div class="title basic">
          Basic
        </div>
        <div class="subtitle">
          Try it out!
        </div>
        <div class="body">
          <ul>Play with up to 5 players per game</ul>
          <ul>Access to basic prompts</ul>
        </div>
        <div class="price-container">
          Free
        </div>
        <transition name="fade">
          <SpinnerButton
            v-show="isUserLeader && isSubscriptionActive && !isSubscriptionEnding"
            class="button danger"
            :showSpinner="false"
            @click="showCancelSubscriptionModal = true"
          >
            Downgrade and Disband
          </SpinnerButton>
        </transition>
        <transition name="fade">
          <div
            v-show="!isUserOnTeam"
            class="button disabled"
          >
            Your Current Plan
          </div>
        </transition>
        <transition name="fade">
          <div
            v-show="isUserOnTeam && isSubscriptionActive && isSubscriptionEnding"
            class="button disabled"
          >
            Your Upcoming Plan
          </div>
        </transition>
      </div>
      <div class="plan-container">
        <div class="title">
          Pro
        </div>
        <div class="subtitle">
          Step up your game
        </div>
        <div class="body">
          <ul>Play with up to 8 players per game</ul>
          <ul>Unlimited games</ul>
          <ul>Create and manage your own team</ul>
          <ul>Write your own prompts and add them to the game</ul>
          <ul>Premium prompt packs: onboarding, happy hour, team building, and icebreaker</ul>
          <ul>Save all scores</ul>
          <ul>Includes 8 seats</ul>
        </div>
        <div class="price-container">
          $24.99 / month
        </div>
        <div class="price-container small">
          Add more seats anytime for $2.99 / month
        </div>
        <transition name="fade">
          <SpinnerButton
            v-show="!isUserOnTeam"
            class="button"
            :showSpinner="false"
            @click="showCreateSubscriptionModal = true"
          >
            Upgrade Your Team
          </SpinnerButton>
        </transition>
        <transition name="fade">
          <SpinnerButton
            v-show="isUserLeader && isSubscriptionActive && isSubscriptionEnterprise"
            class="button"
            :showSpinner="false"
            @click="showSwitchSubscriptionPlanModal = true"
          >
            Switch to Pro
          </SpinnerButton>
        </transition>
        <transition name="fade">
          <SpinnerButton
            v-show="isUserLeader && isSubscriptionActive && isSubscriptionEnding && !isSubscriptionEnterprise"
            class="button"
            :showSpinner="false"
            @click="showRenewSubscriptionModal = true"
          >
            Renew Your Subscription
          </SpinnerButton>
        </transition>
        <transition name="fade">
          <div
            v-show="isUserOnTeam && isSubscriptionActive && !isSubscriptionEnding && !isSubscriptionEnterprise"
            class="button disabled"
          >
            Your Current Plan
          </div>
        </transition>
      </div>
      <div class="plan-container">
        <div class="title">
          Enterprise
        </div>
        <div class="subtitle">
          Fun for all
        </div>
        <div class="body">
          <ul>All Weavr Pro features</ul>
          <ul>Unlimited access for everyone at your company</ul>
          <ul>Three live events facilitated by our team each month</ul>
          <ul>We'll create one new prompt pack just for you each month</ul>
          <div class="tier-dropdown-container">
            <CustomSelect
              v-model="selectedEnterpriseTier"
              :options="enterpriseTierOptions"
            />
          </div>
        </div>
        <div class="price-container">
          ${{ this.enterpriseTierPrices[this.selectedTier - 1] }} / month
        </div>
        <transition name="fade">
          <SpinnerButton
            v-show="!isUserOnTeam"
            class="button"
            :isProcessing="showSpinner"
            @click="upgradeToEnterprisePlan"
          >
            Upgrade to Enterprise
          </SpinnerButton>
        </transition>
        <transition name="fade">
          <SpinnerButton
            v-show="isUserLeader && isSubscriptionActive && !isSubscriptionEnterprise"
            class="button"
            :showSpinner="false"
            @click="showSwitchSubscriptionPlanModal = true"
          >
            Upgrade to Enterprise
          </SpinnerButton>
        </transition>
        <transition name="fade">
          <SpinnerButton
            v-show="isUserLeader && isSubscriptionActive && isSubscriptionEnding && isSubscriptionEnterprise"
            class="button"
            :showSpinner="false"
            @click="showRenewSubscriptionModal = true"
          >
            Renew Your Subscription
          </SpinnerButton>
        </transition>
        <transition name="fade">
          <div
            v-show="isUserOnTeam && isSubscriptionActive && !isSubscriptionEnding && isSubscriptionEnterprise"
            class="button disabled"
          >
            Your Current Plan
          </div>
        </transition>
      </div>
    </div>
    <div class="plan-suggestions-row">
      <div class="plan-suggestion-item small">
        <span>Best for taking Weavr for a test drive</span>
      </div>
      <div class="plan-suggestion-item">
        <span>Best for small teams who want to add their own company culture to the mix</span>
      </div>
      <div class="plan-suggestion-item">
        <span>Best for HR teams looking to use Weavr for engagement across their entire org</span>
      </div>
    </div>
    <CreateSubscriptionModal
      v-if="showCreateSubscriptionModal"
      @close="showCreateSubscriptionModal = false"
    />
    <CancelSubscriptionModal
      v-if="showCancelSubscriptionModal"
      @close="showCancelSubscriptionModal = false"
    />
    <RenewSubscriptionModal
      v-if="showRenewSubscriptionModal"
      @close="showRenewSubscriptionModal = false"
    />
    <SwitchSubscriptionPlanModal
      v-if="showSwitchSubscriptionPlanModal"
      :isSubscriptionEnterprise="isSubscriptionEnterprise"
      :tier="selectedTier"
      @close="showSwitchSubscriptionPlanModal = false"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import SpinnerButton from '@/components/utilComponents/SpinnerButton.vue';
import CustomSelect from '@/components/utilComponents/CustomSelect.vue';
import CreateSubscriptionModal from '@/components/pricingAndTeamComponents/CreateSubscriptionModal.vue';
import CancelSubscriptionModal from '@/components/pricingAndTeamComponents/CancelSubscriptionModal.vue';
import RenewSubscriptionModal from '@/components/pricingAndTeamComponents/RenewSubscriptionModal.vue';
import SwitchSubscriptionPlanModal from '@/components/pricingAndTeamComponents/SwitchSubscriptionPlanModal.vue';

export default Vue.extend({
  name: 'PricingView',
  components: {
    SpinnerButton,
    CustomSelect,
    CreateSubscriptionModal,
    CancelSubscriptionModal,
    RenewSubscriptionModal,
    SwitchSubscriptionPlanModal
  },
  data: () => ({
    stripe: {},
    sessionId: '',
    hasTeamInfoLoaded: false,
    isUserOnTeam: false,
    isUserLeader: false,
    subscriptionInfo: {},
    isSubscriptionInfoAvailable: false,
    selectedEnterpriseTier: null,
    enterpriseTierOptions: [
      '50-100 employees',
      '100-250 employees',
      '250-500 employees',
      '500+ employees'
    ],
    enterpriseTierPrices: [
      225,
      550,
      1000,
      1495
    ],
    showSpinner: false,
    showCreateSubscriptionModal: false,
    showCancelSubscriptionModal: false,
    showRenewSubscriptionModal: false,
    showSwitchSubscriptionPlanModal: false
  }),
  computed: {
    auth0UserId: function () {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    },
    selectedTier: function() {
      return Math.max(this.enterpriseTierOptions.indexOf(this.selectedEnterpriseTier) + 1);
    },
    subscriptionStatus: function() {
      if (this.isSubscriptionInfoAvailable) {
        return this.subscriptionInfo.status || 'incomplete';
      }
      return 'incomplete';
    },
    isSubscriptionActive: function() {
      return ['trialing', 'active', 'past_due', 'unpaid'].includes(this.subscriptionStatus);
    },
    isSubscriptionEnding: function() {
      if (this.isSubscriptionInfoAvailable) {
        return this.subscriptionInfo.cancelAtPeriodEnd || false;
      }
      return false;
    },
    isSubscriptionEnterprise: function() {
      if (this.isSubscriptionInfoAvailable) {
        return this.subscriptionInfo.hasUnlimitedSeats || false;
      }
      return false;
    }
  },
  methods: {
    async checkUserIsOnTeam() {
      const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/checkuserisonteam`, {
        auth0UserId: this.auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });
      this.isUserOnTeam = response.data.isUserOnTeam;
      this.isUserLeader = response.data.isUserLeader;
    },
    async getSubscriptionInfo() {
      const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getsubscriptioninfo`, {
        auth0UserId: this.auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });

      this.subscriptionInfo = response.data.subscriptionInfo;

      if (Object.keys(this.subscriptionInfo).length > 0) {
        this.isSubscriptionInfoAvailable = true;
      }
    },
    async upgradeToEnterprisePlan() {
      try {
        this.showSpinner = true;
        await this.ensureStripeCustomer();
        await this.getSessionId();
        await this.checkout();
      } catch (error) {
        console.log('Error:', error);
        this.showSpinner = false;
      }
    },
    async ensureStripeCustomer() {
      await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/ensurestripecustomer`, {
        auth0UserId: this.auth0UserId,
        emailAddress: this.$auth.user.email
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });
    },
    async getSessionId() {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/createcheckoutsessionenterprise`, {
          auth0UserId: this.auth0UserId,
          tier: this.selectedTier
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
        this.sessionId = response.data.sessionId;
      } catch (error) {
        console.log('Error:', error);
      }
    },
    checkout() {
      if (this.sessionId.length > 0) {
        this.stripe.redirectToCheckout({
          sessionId: this.sessionId
        });
      }
    },
  },
  created: async function() {
    this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
    this.selectedEnterpriseTier = this.enterpriseTierOptions[0];
    await this.checkUserIsOnTeam();
    if (this.isUserLeader) {
      await this.getSubscriptionInfo();
    }
    this.hasTeamInfoLoaded = true;
  }
})
</script>

<style scoped lang="scss">
@import '@/styles/pricing-screen.scss';
</style>
